import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import { Separator } from "@/shared/components/ui/separator";
import {
  DocumentNotReadyAlert,
  DocumentSheet,
  DocumentSheetActions,
  DocumentSheetActionsContent,
  DocumentSheetActionsDownload,
  DocumentSheetActionsHeader,
  DocumentSheetActionsSetImmutableMutation,
  DocumentSheetContent,
  DocumentSheetHeader,
  DocumentSheetImmutabilityWarning,
  DocumentSheetMain,
  DocumentSheetPreview,
  DocumentSheetTitle,
  DocumentSheetTrigger,
} from "@/shared/bsm/core/document/components/ui/document-sheet";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { Download } from "lucide-react";
import { useDownloadPdf } from "@/shared/lib/utilities/download-pdf";
import { useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import {
  rtkErrIsErrDocumentNotReady,
  useErrDocumentNotReady,
  useSetErrDocumentNotReadyFromRtk,
} from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { Report } from "@/services/backend/htz/report/types/report";
import {
  ReportDocument,
  ReportImmutabilityReason,
  useHtzReportPdfDownloadMutation,
  useHtzReportPdfPreviewQuery,
  useHtzReportSetImmutableMutation,
} from "@/services/backend/htz/report/api/report-api";

export function ReportPDF({ report }: { report: Report }) {
  const { data, isLoading, error } = useHtzReportPdfPreviewQuery({
    reportId: report.id,
    document: ReportDocument.ALL,
  });

  const { notReadyError, setNotReadyError } = useErrDocumentNotReady();

  const { user } = useAuth();

  return (
    <DocumentSheet>
      <DocumentSheetTrigger asChild>
        <Button variant="outline">PDF</Button>
      </DocumentSheetTrigger>
      <DocumentSheetContent className="max-h-[90vh]">
        <DocumentSheetHeader>
          <DocumentSheetTitle>{t("Download PDF")}</DocumentSheetTitle>
        </DocumentSheetHeader>
        <DocumentSheetMain>
          <DocumentSheetPreview
            objectURL={data?.objectURL}
            isLoading={isLoading}
            error={error}
          />
          <DocumentSheetActions>
            <DocumentSheetActionsHeader
              title={t("Bericht")}
              documentNumber={<DocumentNumberById documentId={report.id} />}
            />
            <DocumentSheetActionsContent>
              {!report.immutable && (
                <>
                  <DocumentNotReadyAlert
                    error={notReadyError}
                    document={t("Bericht")}
                  />
                  <DocumentSheetImmutabilityWarning
                    title={t("Hinweis")}
                    description={t(
                      `Um eine Berichtnummer zu generieren müssen Sie den Bericht festschreiben. Sobald der Bericht festgeschrieben ist, wird das Wasserzeichen "Vorschau" entfernt.`,
                    )}
                  />
                  <Separator />
                  <DocumentSheetActionsSetImmutableMutation
                    request={{
                      id: report.id,
                      reason: ReportImmutabilityReason.SetByUser,
                      setBy: user?.id || "",
                    }}
                    mutation={useHtzReportSetImmutableMutation}
                    description={t(
                      "Der Bericht wird festgeschrieben und es wird eine Berichtnummer generiert. Danach kann der Bericht nicht mehr geändert werden.",
                    )}
                    onError={(err) => {
                      if (rtkErrIsErrDocumentNotReady(err)) {
                        setNotReadyError(err.data);
                      }
                    }}
                  />
                </>
              )}
              <DocumentSheetActionsDownload>
                <DownloadDocumentButton
                  reportId={report.id}
                  document={ReportDocument.ALL}
                  text={t("Alle Dokumente")}
                />
                <DownloadDocumentButton
                  reportId={report.id}
                  document={ReportDocument.REPORT}
                  text={t("Bericht")}
                />
              </DocumentSheetActionsDownload>
            </DocumentSheetActionsContent>
          </DocumentSheetActions>
        </DocumentSheetMain>
      </DocumentSheetContent>
    </DocumentSheet>
  );
}

function DownloadDocumentButton({
  reportId,
  document: documentType,
  text,
}: {
  reportId: string;
  document: ReportDocument;
  text: string;
}) {
  const [open, setOpen] = useState(false);
  const [download, { isLoading, error, data, reset }] =
    useHtzReportPdfDownloadMutation();

  useDownloadPdf(data, () => {
    setOpen(false);
    reset();
  });

  useSetErrDocumentNotReadyFromRtk(error, () => {
    setOpen(false);
    reset();
  });

  const doDownload = () => {
    if (isLoading) {
      return;
    }

    download({
      reportId,
      document: documentType,
      download: true,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="justify-start">
          <Download className="mr-4 h-5 w-5" /> {text}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Bericht herunterladen")}</DialogTitle>
          <DialogDescription>
            {t("Möchten Sie den Bericht als PDF herunterladen?")}
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doDownload} disabled={isLoading}>
            {isLoading ? (
              <Download className="mr-4 h-5 w-5 animate-bounce" />
            ) : (
              <Download className="mr-4 h-5 w-5" />
            )}
            <span>{t("Herunterladen")}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
